// Remuneraciones: libro de sueldos (xlsx) ↔ depósitos banco ↔ egreso global Alegra (Iter 353)
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Upload, Loader2, CheckCircle2, Trash2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { AnticiposSection } from './AnticiposSection';

const CLP = (v) => (v || 0).toLocaleString('es-CL');
const ddmm = (f) => fmtFecha(f);
const ESTADO = {
  ok: { cls: 'bg-emerald-100 text-emerald-700', label: 'OK' },
  diferencia: { cls: 'bg-orange-100 text-orange-700', label: 'Diferencia' },
  sin_deposito: { cls: 'bg-red-100 text-red-700', label: 'Sin depósito' },
  sin_pago_esperado: { cls: 'bg-gray-100 text-gray-500', label: 'Sin pago' },
  finiquito: { cls: 'bg-slate-200 text-slate-600', label: 'Finiquito / fuera' },
};
const CONCEPTO = { liquido: 'Líquido', extras: 'Extras', 'liquido+extras': 'Líq.+Extras', anticipo: 'Anticipo', otro: 'Otro monto' };
const dif = (v) => (v === 0 ? 'text-emerald-600' : 'text-red-600');

function Totales({ t }) {
  const cards = [
    ['Nómina (líq.+extras)', `$${CLP(t.nomina)}`, '', 'nomina'],
    ['Banco asignado', `$${CLP(t.banco)}`, dif(t.dif_banco_nomina), 'banco'],
    ['Egresos Alegra', `$${CLP(t.alegra)}`, dif(t.dif_alegra_nomina), 'alegra'],
    ['Banco − Nómina', `$${CLP(t.dif_banco_nomina)}`, dif(t.dif_banco_nomina), 'dif-banco-nomina'],
    ['Alegra − Nómina', `$${CLP(t.dif_alegra_nomina)}`, dif(t.dif_alegra_nomina), 'dif-alegra-nomina'],
    ['Depósitos sin nómina', `$${CLP(t.banco_sin_nomina)}`, t.banco_sin_nomina ? 'text-amber-600' : '', 'sin-nomina'],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-6" data-testid="rem-totales">
      {cards.map(([label, val, cls, tid]) => (
        <Card key={label}><CardContent className="p-3" data-testid={`rem-card-${tid}`}>
          <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
          <p className={`text-base font-bold ${cls}`}>{val}</p>
        </CardContent></Card>
      ))}
    </div>
  );
}

function TablaTrabajadores({ filas }) {
  return (
    <Card><CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-sm" data-testid="rem-tabla-trabajadores">
        <thead><tr className="border-b bg-slate-50 text-left text-xs text-muted-foreground">
          <th className="px-3 py-2">Trabajador</th><th className="px-3 py-2">Empresa</th>
          <th className="px-3 py-2 text-right">Líquido</th><th className="px-3 py-2 text-right">Extras</th>
          <th className="px-3 py-2 text-right">Banco</th><th className="px-3 py-2 text-right">Dif.</th>
          <th className="px-3 py-2">Estado</th><th className="px-3 py-2">Depósitos</th>
        </tr></thead>
        <tbody>
          {filas.map((f) => {
            const e = ESTADO[f.estado];
            return (
              <tr key={f.rut} className="border-b last:border-0 align-top" data-testid={`rem-row-${f.rut}`}>
                <td className="px-3 py-1.5"><p className="font-medium">{f.nombre}</p><p className="text-[11px] text-muted-foreground">{f.rut}{f.finiquito ? ' · finiquito' : ''}</p></td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">{f.hoja}</td>
                <td className="px-3 py-1.5 text-right">${CLP(f.liquido)}</td>
                <td className="px-3 py-1.5 text-right">{f.extras ? `$${CLP(f.extras)}` : '—'}</td>
                <td className="px-3 py-1.5 text-right font-medium">${CLP(f.banco)}</td>
                <td className={`px-3 py-1.5 text-right ${f.diferencia ? 'text-red-600' : 'text-muted-foreground'}`}>{f.diferencia ? `$${CLP(f.diferencia)}` : '—'}</td>
                <td className="px-3 py-1.5"><Badge className={`${e.cls} text-[11px]`} data-testid={`rem-estado-${f.rut}`}>{e.label}</Badge></td>
                <td className="px-3 py-1.5 text-xs">
                  {f.depositos.map((d) => (
                    <p key={d.mov_id} className={`whitespace-nowrap ${d.fuera_fecha || d.concepto === 'anticipo' ? 'text-muted-foreground line-through' : ''}`} title={d.fuera_fecha ? 'Fuera de la fecha de pago: no suma' : d.concepto === 'anticipo' ? 'Anticipo: no suma' : ''}>{ddmm(d.fecha)} · ${CLP(d.monto)} · <span className="text-muted-foreground">{CONCEPTO[d.concepto]}</span>{d.estado_mov !== 'pendiente' && <CheckCircle2 className="ml-1 inline h-3 w-3 text-emerald-600" />}</p>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

function Listas({ c, onToggleAlegra }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card><CardContent className="p-3 space-y-2" data-testid="rem-sin-nomina">
        <p className="text-sm font-semibold">Depósitos en fechas de pago a personas que no están en el libro ({c.depositos_sin_nomina.length})</p>
        <p className="text-[11px] text-muted-foreground">Fechas de pago detectadas: {c.fechas_pago.map(ddmm).join(', ') || '—'}</p>
        {c.depositos_sin_nomina.length === 0 && <p className="text-xs text-muted-foreground">Ninguno.</p>}
        {c.depositos_sin_nomina.map((m) => (
          <div key={m.mov_id} className="text-xs border-b pb-1 last:border-0" data-testid={`rem-sin-nomina-${m.mov_id}`}>
            <span className="text-muted-foreground">{ddmm(m.fecha)}</span> {m.detalle.replace(/^TRASPASO A:/i, '')} · <b>${CLP(m.monto)}</b>
            {m.pista && <p className="text-amber-700">{m.pista}</p>}
          </div>
        ))}
        {c.ambiguos.length > 0 && <p className="text-xs text-amber-700">{c.ambiguos.length} depósito(s) con nombre ambiguo: {c.ambiguos.map((a) => a.detalle).join(' | ')}</p>}
      </CardContent></Card>
      <Card><CardContent className="p-3 space-y-2" data-testid="rem-egresos-alegra">
        <p className="text-sm font-semibold">Egresos relacionados en Alegra — marca los que forman el global de sueldos</p>
        {c.egresos_alegra.length === 0 && <p className="text-xs text-muted-foreground">No se detectaron egresos con "sueldo/remuneración" en la ventana.</p>}
        {c.egresos_alegra.map((e) => (
          <label key={e.alegra_id} className={`flex cursor-pointer items-start justify-between gap-2 border-b pb-1 text-xs last:border-0 ${e.incluido ? '' : 'text-muted-foreground'}`} data-testid={`rem-egreso-${e.alegra_id}`}>
            <span className="flex gap-2">
              <input type="checkbox" checked={e.incluido} onChange={() => onToggleAlegra(e)} className="mt-0.5" data-testid={`rem-egreso-check-${e.alegra_id}`} />
              <span><span className="text-muted-foreground">{ddmm(e.fecha)}</span> #{e.alegra_id} {e.descripcion || e.contacto} <span className="text-muted-foreground">({e.cuentas.join(', ')})</span></span>
            </span>
            <b className="whitespace-nowrap">${CLP(e.monto)}</b>
          </label>
        ))}
      </CardContent></Card>
    </div>
  );
}

export function RemuneracionesTab({ onChanged }) {
  const [nominas, setNominas] = useState([]);
  const [sel, setSel] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [cruce, setCruce] = useState(null);
  const [alegraIds, setAlegraIds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const fileRef = useRef(null);

  const loadNominas = useCallback(async () => {
    const r = await api.get('/conciliacion-bancaria/remuneraciones');
    setNominas(r.data.nominas || []);
    return r.data.nominas || [];
  }, []);

  useEffect(() => {
    loadNominas().then((ns) => { if (ns.length && !sel) setSel(ns[0].id); }).catch(() => toast.error('Error cargando nóminas'));
  }, [loadNominas]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const n = nominas.find((x) => x.id === sel);
    if (n) { setDesde(n.ventana_default.desde); setHasta(n.ventana_default.hasta); setAlegraIds(null); }
  }, [sel, nominas]);

  const loadCruce = useCallback(async () => {
    if (!sel || !desde || !hasta) return;
    setLoading(true);
    try {
      const params = { desde, hasta };
      if (alegraIds !== null) params.alegra_ids = alegraIds.join(',');
      const r = await api.get(`/conciliacion-bancaria/remuneraciones/${sel}/cruce`, { params });
      setCruce(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error calculando cruce'); }
    finally { setLoading(false); }
  }, [sel, desde, hasta, alegraIds]);
  useEffect(() => { loadCruce(); }, [loadCruce]);

  const toggleAlegra = (e) => {
    const actuales = cruce.egresos_alegra.filter((x) => x.incluido).map((x) => x.alegra_id);
    setAlegraIds(e.incluido ? actuales.filter((id) => id !== e.alegra_id) : [...actuales, e.alegra_id]);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/conciliacion-bancaria/remuneraciones/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      toast.success(r.data.message);
      await loadNominas();
      setSel(r.data.nomina.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error subiendo el libro'); }
    finally { setSubiendo(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleAplicar = async () => {
    if (!cruce) return;
    const exactos = cruce.trabajadores.reduce((n, f) => n + f.depositos.filter((d) => ['liquido', 'extras', 'liquido+extras'].includes(d.concepto) && d.estado_mov === 'pendiente').length, 0);
    if (!window.confirm(`Se conciliarán ${exactos} depósitos pendientes con monto exacto y se marcará el período en los ya conciliados. ¿Continuar?`)) return;
    setAplicando(true);
    try {
      const r = await api.post(`/conciliacion-bancaria/remuneraciones/${sel}/aplicar`, { desde, hasta });
      toast.success(`${r.data.conciliados} depósitos conciliados, ${r.data.marcados} marcados con período`);
      loadCruce();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error aplicando'); }
    finally { setAplicando(false); }
  };

  const handleEliminar = async () => {
    const n = nominas.find((x) => x.id === sel);
    if (!n || !window.confirm(`¿Eliminar el libro ${n.periodo_label}? (no afecta las conciliaciones ya aplicadas)`)) return;
    try {
      await api.delete(`/conciliacion-bancaria/remuneraciones/${sel}`);
      toast.success('Libro eliminado');
      setCruce(null); setSel('');
      loadNominas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error eliminando'); }
  };

  return (
    <div className="space-y-4" data-testid="remuneraciones-tab">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={handleUpload} data-testid="input-libro-file" />
        <Button onClick={() => fileRef.current?.click()} disabled={subiendo} data-testid="btn-subir-libro">
          {subiendo ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Subir libro de remuneraciones (.xlsx)
        </Button>
        {nominas.length > 0 && (
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="w-56" data-testid="select-nomina"><SelectValue placeholder="Período" /></SelectTrigger>
            <SelectContent>{nominas.map((n) => <SelectItem key={n.id} value={n.id}>{n.periodo_label} · {n.empresas.length} empresa(s)</SelectItem>)}</SelectContent>
          </Select>
        )}
        {sel && (
          <>
            <span className="text-xs text-muted-foreground">Depósitos entre</span>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" data-testid="input-rem-desde" />
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" data-testid="input-rem-hasta" />
            <Button variant="outline" onClick={handleAplicar} disabled={aplicando || !cruce} data-testid="btn-aplicar-remuneraciones">
              {aplicando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Link2 className="mr-1 h-4 w-4" />} Aplicar conciliación
            </Button>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={handleEliminar} data-testid="btn-eliminar-libro"><Trash2 className="mr-1 h-3.5 w-3.5" /> Eliminar libro</Button>
          </>
        )}
      </div>

      {nominas.length === 0 && !subiendo && (
        <p className="p-6 text-sm text-muted-foreground" data-testid="rem-empty">Sube el libro de remuneraciones (xlsx con columnas Nombre Trabajador, Rut, Alcance Líquido, extras) para cruzarlo con los depósitos del banco y el egreso de Alegra.</p>
      )}
      {loading && <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground" data-testid="rem-loading"><Loader2 className="h-4 w-4 animate-spin" /> Cruzando nómina con banco y Alegra...</div>}
      {cruce && !loading && (
        <>
          <p className="text-xs text-muted-foreground" data-testid="rem-resumen">
            {cruce.nomina.periodo_label} · {cruce.nomina.archivo} · {cruce.resumen.trabajadores} trabajadores: <b className="text-emerald-700">{cruce.resumen.ok} OK</b>, <b className="text-orange-700">{cruce.resumen.diferencia} con diferencia</b>, <b className="text-red-700">{cruce.resumen.sin_deposito} sin depósito</b>, {cruce.resumen.finiquito} finiquito/fuera de nómina (no suman al total), {cruce.resumen.sin_pago_esperado} sin pago esperado
          </p>
          <Totales t={cruce.totales} />
          <TablaTrabajadores filas={cruce.trabajadores} />
          <Listas c={cruce} onToggleAlegra={toggleAlegra} />
          <AnticiposSection nominaId={sel} periodo={cruce.nomina.periodo} onChanged={onChanged} />
        </>
      )}
    </div>
  );
}
